import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { logEvent } from "../../utils/analytics";

const METRIC_CARDS = [
  {
    label: "Simulation Speed", value: "< 2s", sub: "per full shipment", icon: "bolt",
    description: "A full container mix and 3D packing plan, computed in under two seconds — however many SKUs you throw at it.",
  },
  {
    label: "3D Visualization", value: "Live", sub: "watch it load, in real time", icon: "3d_rotation",
    description: "Rotate, zoom, and see every carton before you commit — not just a number, the actual packing plan in 3D.",
  },
  {
    label: "To Start", value: "Free", sub: "no card, no signup", icon: "rocket_launch",
    description: "Run real simulations on up to 2 containers and 2 item types — free, forever, no card, no account.",
  },
];

// Phrases lifted straight from the old hero entry-card (removed — see the
// CTA below), so the card's "wow, come look at examples" personality isn't
// lost, just carried by the button's label instead of a whole separate
// panel. Cycles on a timer, crossfading, same hand-rolled setInterval +
// CSS-transition style as MetricCard's typing effect above (no animation
// library in this codebase). min-w reserves space for the longest phrase so
// the button doesn't resize and jiggle the CTA row as the label rotates.
const EXPLORE_CTA_PHRASES = [
  'See it in action',
  "Can't make up your mind?",
  '7 shipments, packed',
];

function FlipCtaLabel({ phrases, intervalMs = 2600 }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const tick = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % phrases.length);
        setVisible(true);
      }, 200);
    }, intervalMs);
    return () => clearInterval(tick);
  }, [phrases, intervalMs]);

  return (
    <span
      className={`inline-block min-w-[220px] text-center transition-all duration-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`}
    >
      {phrases[index]}
    </span>
  );
}

function MetricCard({ card }) {
  const [typedText, setTypedText] = useState("");
  const intervalRef = useRef(null);

  const startTyping = () => {
    clearInterval(intervalRef.current);
    setTypedText("");
    let i = 0;
    intervalRef.current = setInterval(() => {
      i += 1;
      setTypedText(card.description.slice(0, i));
      if (i >= card.description.length) clearInterval(intervalRef.current);
    }, 15);
  };

  const stopTyping = () => {
    clearInterval(intervalRef.current);
    setTypedText("");
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  return (
    <div
      className="group relative flex flex-col gap-3 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-white/10 via-white/5 to-transparent p-8 backdrop-blur-xl transition-all hover:border-primary/60 hover:scale-105 hover:shadow-2xl hover:shadow-primary/30 overflow-hidden"
      onMouseEnter={startTyping}
      onMouseLeave={stopTyping}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
      <div className="relative z-10 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{card.label}</p>
      </div>
      {/* Kept above the hover panel (z-30 > panel's z-20) so it stays visible
          through the reveal, recolored on hover for contrast against the
          blue gradient instead of disappearing as blue-on-blue. */}
      <span
        className="material-symbols-outlined icon-shimmer absolute right-5 top-5 z-30 text-2xl text-primary transition-colors duration-300 group-hover:text-white"
        style={{ fontVariationSettings: "'FILL' 1" }}
      >
        {card.icon}
      </span>
      <p className="relative z-10 text-4xl font-black text-white tracking-tight">{card.value}</p>
      <p className="relative z-10 text-sm text-slate-400">{card.sub}</p>

      {/* Hover reveal — panel wipes in via clip-path, then the description
          types out character by character instead of sliding or fading in. */}
      <div className="absolute inset-0 z-20 flex items-center overflow-hidden bg-gradient-to-br from-primary via-blue-600 to-cyan-600 p-8 transition-[clip-path] duration-150 ease-out [clip-path:inset(0_100%_0_0)] group-hover:[clip-path:inset(0_0_0_0)]">
        <p className="pr-6 text-sm font-semibold leading-relaxed text-white">
          {typedText}
          {typedText.length > 0 && typedText.length < card.description.length && (
            <span className="ml-0.5 inline-block h-[1em] w-[2px] translate-y-[2px] animate-pulse bg-white align-middle" />
          )}
        </p>
      </div>
    </div>
  );
}

export function HeroSection() {
  const { isAuthenticated } = useAuth();

  return (
    <section id="top" className="relative flex min-h-screen flex-col justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-background-dark via-background-dark/85 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-transparent to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-cyan-500/5 z-10" />
        <div
          className="h-full w-full bg-cover bg-center scale-105"
          style={{ backgroundImage: `url('https://lh3.googleusercontent.com/aida-public/AB6AXuDyLpT7xzWuLXSjVnck9hdaoGlhcFPgMOG7eVlwFbYjm8CzVvaB3GKAeVAwwgSkKsCmNjgSV67ziA8uXWebBxWLEfbzqHTm2u4HyAV8TxEPhRj0BzKRj0_OyMfynJY8RMTbnSCxqtfJKJ64Ga39Sv3-RWhBQfjPLkcuF2kLpl85rgQZmw7c7qDDsE-CE0RD4D2D0rYLq1kYKqnm2dEKYfP08s2O-xNYXKzWf5hgEIoY_t7N5NhYsGxqhp6kcsTaicNBchTblVEXx3UJ')` }}
        />
        <div className="absolute inset-0 z-10">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary/30 rounded-full animate-pulse" />
          <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-cyan-400/40 rounded-full animate-pulse delay-300" />
          <div className="absolute bottom-1/4 left-1/3 w-1 h-1 bg-blue-400/50 rounded-full animate-pulse delay-700" />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-20 mx-auto w-full max-w-7xl px-6 md:px-16 pt-36 pb-32">
        <div className="max-w-3xl">
          {/* Headline */}
          <h1 className="mb-5 text-4xl font-black leading-[1.15] tracking-tight text-white md:text-5xl lg:text-6xl">
            Stop{" "}
            <span className="text-flicker">guessing</span>
            {" "}which<br />
            containers to book.
          </h1>

          {/* Sub — "beyond CBM" hook */}
          <p className="mb-3 text-lg leading-relaxed text-slate-300 md:text-xl max-w-xl font-medium">
            CBM tells you volume.{" "}
            <span className="text-white font-bold">Packing fills space.</span>{" "}
            But the{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-primary bg-clip-text text-transparent font-bold">
              cheapest container mix, packed to maximum density
            </span>{" "}
            — that's where the real money is saved.
          </p>

          <p className="mb-10 text-base text-slate-400 max-w-lg leading-relaxed">
            The only engine that computes the cheapest container mix
            with the most space-efficient packing plan —{" "}
            <span className="text-slate-200 font-semibold">in one click.</span>{" "}
            Used in production by global supply chain teams.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4">
            <Link
              to={isAuthenticated ? '/console' : '/3d-cargo-load-simulator'}
              onClick={() => logEvent({ name: isAuthenticated ? 'hero_open_console' : 'hero_try_free', type: 'cta' })}
              className="group flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary via-blue-500 to-cyan-500 bg-[length:200%_200%] animate-gradient px-8 py-4 text-base font-bold text-white shadow-xl shadow-primary/30 transition-all hover:shadow-2xl hover:shadow-primary/50 hover:scale-105 active:scale-95"
            >
              {isAuthenticated ? 'Open Console' : 'Run the engine'}
              <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">arrow_forward</span>
            </Link>
            {/* Was a link out to a static Drive video — swapped for /examples,
                which now does the "show me it works" job better: interactive
                (spin/zoom/pause across 7 real shipments) instead of passive,
                and it never goes stale the way a recorded video would every
                time the product changes. */}
            {!isAuthenticated && (
              <Link
                to="/examples"
                onClick={() => logEvent({ name: 'hero_see_examples', type: 'cta' })}
                className="group flex items-center gap-2 rounded-lg border-2 border-primary/50 bg-slate-800/80 backdrop-blur-md px-6 py-4 text-base font-bold text-white transition-all hover:border-primary hover:bg-slate-700/80 hover:scale-105 active:scale-95 hover:shadow-lg hover:shadow-primary/20"
              >
                <span className="material-symbols-outlined shrink-0 animate-pulse transition-transform group-hover:scale-110">sports_esports</span>
                <FlipCtaLabel phrases={EXPLORE_CTA_PHRASES} />
              </Link>
            )}
          </div>
        </div>

        {/* Metric cards — hover reveals a description that types out
            character by character over a panel that wipes in via clip-path. */}
        <div className="mt-20 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {METRIC_CARDS.map((card) => (
            <MetricCard key={card.label} card={card} />
          ))}
        </div>
      </div>
    </section>
  );
}
